import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { Fragment } from 'react';

import { GlobalHeadwindsSection } from '@/components/ui/features/publishers/GlobalHeadwindsSection/GlobalHeadwindsSection';
import { PublishersHeader } from '@/components/ui/features/publishers/PublishersHeader/PublishersHeader';
import { ReadyToTransform } from '@/components/ui/features/publishers/ReadyToTransform/ReadyToTransform';
import { ReasonsSection } from '@/components/ui/features/publishers/ReasonsSection/ReasonsSection';
import { TripleRSection } from '@/components/ui/features/publishers/TripleRSection/TripleRSection';
import { Layout } from '@/components/ui/shared/layouts/Layout/Layout';
import { JsonLd } from '@/components/ui/shared/seo/JsonLd/JsonLd';
import { AccessFrom } from '@/enums/AccessFrom';
import { queryTestimonials } from '@/queries/testimonals';
import { generateStore } from '@/utils/generateStore';
import { buildBreadcrumbSchema, buildWebPageSchema, getAbsoluteUrl } from '@/utils/structured-data';
import { Hydrate } from '@/utils/swr/Hydrate';

const PAGE_PATH = 'publishers';
const PAGE_TITLE = 'Publishers';
const PAGE_DESCRIPTION =
  'Record, stream, and share your scientific conference with global researchers who cannot attend in person. Underline Science helps societies and publishers build a permanent, citable digital archive of their proceedings.';

const ForPublishers: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = ({ fallback }) => {
  return (
    <Fragment>
      <NextSeo
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        canonical={getAbsoluteUrl(PAGE_PATH)}
        openGraph={{
          url: getAbsoluteUrl(PAGE_PATH),
          title: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
        }}
      />
      <JsonLd
        id="publishers"
        schema={[
          buildWebPageSchema({ path: PAGE_PATH, name: PAGE_TITLE, description: PAGE_DESCRIPTION }),
          buildBreadcrumbSchema([
            { name: 'Home', path: '' },
            { name: 'Publishers', path: PAGE_PATH },
          ]),
        ]}
      />
      <Hydrate fallback={fallback}>
        <Layout accessFrom={AccessFrom.Core} navigationVariant="ghost">
          <PublishersHeader />
          <GlobalHeadwindsSection />
          <TripleRSection />
          <ReasonsSection />
          <ReadyToTransform />
        </Layout>
      </Hydrate>
    </Fragment>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const store = generateStore({ context });

  await store.fetchResources(queryTestimonials());

  return {
    props: {
      fallback: store.fallback,
    },
  };
};
export default ForPublishers;
